// renderableToast — hàng rào cuối ở CHỖ VẼ toast của host: `message` mà React không vẽ được thì đổi sang
// chữ đọc được TRƯỚC khi tới <ToastBar>, và báo nguồn để còn sửa chỗ gọi.
//
// ══ Vì sao phải có (15/09/2026) ══
// react-hot-toast đặt `message` làm CON của phần tử React. Một OBJECT lọt vào là React #31 «Objects are not
// valid as a React child». Cả 3 host đặt <Toaster> NGOÀI mọi error boundary — Work + Connect: anh em của
// <BrowserRouter>, ngoài `Sentry.ErrorBoundary`; Core: gốc không có boundary nào — nên React gỡ CẢ cây:
// trắng NGUYÊN app, không có cả màn «Đã có lỗi xảy ra», phải F5. Dựng lại trên bundle prod hub.cnvwork.com
// (14/09): một toast mang thân lỗi 403 của SaaS ⇒ #root 5759 → 0 ký tự.
// Nguồn object hay gặp nhất: `toast.error(e.response?.data?.error || '…')` — Core trả `error` là CHUỖI nên
// chạy đúng ở Core, còn SaaS trả `{ code, message, details }`. Luật lint chỉ bắt được dạng viết thẳng; đi
// qua biến trung gian là lọt. Nên chặn ở chỗ VẼ: một lần cho mọi chỗ gọi, kể cả code chưa viết (cùng tinh
// thần `tOptions` trong i18n/runtime.js).
//
// ⛔ ĐỪNG thay bằng error boundary im lặng quanh <Toaster> (kiểu BackgroundWidget): boundary bắt được là tắt
// cả vùng toast tới lúc F5 ⇒ mọi thông báo SAU đó biến mất, bấm Lưu không thấy gì — hỏng im lặng hoàn toàn.
//
// ══ Đổi gì ══
//   vẽ được (chuỗi · số · phần tử React · null · mảng các thứ đó)  → trả NGUYÊN `toast`, cùng tham chiếu
//   MessageDescriptor — `msg`…`` quên `i18n._()`                   → dịch ra đúng câu định hiện
//   object · Error · lỗi axios có câu người viết                   → câu đó
//   còn lại                                                         → `fallback` (host dịch sẵn bằng catalog của host)
// Message dạng hàm (`toast((t) => <div/>)`) được gọi thử để soi KẾT QUẢ — hàm trả object cũng sập y hệt.
//
// ══ Báo nguồn ══
// `console.error` kèm nguyên giá trị + `onIssue(issue)` của host (Work/Connect: Sentry.captureMessage).
// `issue` CỐ Ý không mang chữ trong message (có thể dính email, tên khách) — chỉ loại, danh sách khoá, `code`,
// loại toast, route. Breadcrumb XHR của Sentry chỉ ra request vừa hỏng, đủ lần tới chỗ gọi.
// Mỗi toast báo MỘT lần: Toaster vẽ lại mỗi lần đo chiều cao hay rê chuột, báo theo lượt vẽ là ngập Sentry.

import { isValidElement } from 'react';

import { tLabel } from '../i18n/runtime.js';
import { currentRouteTag } from '../lib/errorReporter.js';

const REACT_PORTAL = Symbol.for('react.portal');
const REACT_LAZY = Symbol.for('react.lazy');

/** Đúng tập React 18 nhận làm con. Ngoài tập này: object là #31, hàm/symbol thì vẽ ra rỗng. */
function isRenderable(v, depth = 0) {
  if (v == null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return true;
  if (typeof v !== 'object' || depth > 8) return false;
  if (Array.isArray(v)) return v.every((x) => isRenderable(x, depth + 1));
  return isValidElement(v) || v.$$typeof === REACT_PORTAL || v.$$typeof === REACT_LAZY;
}

/**
 * Chữ máy toàn chữ thường ASCII ('bad input', 'internal', 'flow not found', mã lỗi) hiện ra còn tệ hơn câu
 * dự phòng — cùng luật với apiErrorMessage: chỉ nhận chuỗi có chữ hoa hoặc chữ ngoài ASCII (tiếng Việt có dấu).
 */
function isHumanText(s) {
  if (typeof s !== 'string' || !s.trim()) return false;
  if (/[A-Z]/.test(s)) return true;
  for (let i = 0; i < s.length; i += 1) if (s.charCodeAt(i) > 127) return true;
  return false;
}

/**
 * Câu người viết trong một object lỗi, đọc theo thứ tự: thân lỗi axios (`response.data`) → `message` →
 * `error` dạng chuỗi (Core route cũ) → `error.message` (thân lỗi SaaS `{ error: { code, message } }`).
 */
function humanTextOf(v, depth = 0) {
  if (!v || typeof v !== 'object' || depth > 3) return null;
  return humanTextOf(v.response?.data, depth + 1)
    || (isHumanText(v.message) ? v.message : null)
    || (isHumanText(v.error) ? v.error : null)
    || humanTextOf(v.error, depth + 1);
}

const reported = new Set();

function report(toast, raw, issue, onIssue) {
  const key = `${issue.toastId}|${issue.kind}|${issue.keys}|${issue.shown}`;
  if (reported.has(key)) return;
  if (reported.size >= 200) reported.clear();
  reported.add(key);
  console.error(
    `[toast] message React không vẽ được (${issue.kind}) — đã đổi sang chữ trước khi vẽ, không thì trắng cả app. `
      + 'Chỗ gọi toast phải đọc lỗi thành CHUỖI.',
    { ...issue, message: raw },
  );
  if (typeof onIssue !== 'function') return;
  try {
    onIssue(issue);
  } catch { /* lớp báo lỗi không được phá chính thứ nó đang cứu */ }
}

/**
 * Toast mà <ToastBar> vẽ được. Gọi trong component con của render-prop <Toaster> (xem main.jsx của host).
 *
 * @param {object} toast  phần tử Toaster đưa vào render-prop (`id`, `type`, `message`…)
 * @param {{ fallback?: string, onIssue?: (issue: {
 *   kind: string, keys: string, code: string, shown: 'descriptor'|'text'|'fallback',
 *   toastType: string, toastId: string, route: string }) => void }} [opts]
 *   fallback — câu hiện khi không đọc được chữ nào; HOST dịch sẵn.
 *   onIssue  — nơi host bắn lỗi đi (Sentry). Gọi tối đa một lần cho mỗi toast.
 * @returns {object} chính `toast` nếu message vẽ được; không thì bản sao có `message` là chuỗi
 */
export function renderableToast(toast, { fallback = '', onIssue } = {}) {
  if (!toast) return toast;
  let raw = toast.message;
  let threw = false;
  if (typeof raw === 'function') {
    try {
      raw = raw(toast);
    } catch (err) {
      raw = err;
      threw = true;
    }
  }
  if (!threw && isRenderable(raw)) return toast;

  let message = fallback;
  let shown = 'fallback';
  let kind = threw ? 'throw' : Array.isArray(raw) ? 'array' : raw instanceof Error ? 'error' : typeof raw;
  let keys = '';
  let code = '';
  // Hàng rào mà tự ném thì React sập đúng như lúc chưa có nó ⇒ mọi bước đọc đều bọc try.
  try {
    if (!threw) {
      const translated = tLabel(raw);
      if (typeof translated === 'string') {
        message = translated;
        shown = 'descriptor';
        kind = 'descriptor';
      } else {
        const text = humanTextOf(raw);
        if (text) {
          message = text;
          shown = 'text';
        }
      }
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      keys = Object.keys(raw).slice(0, 8).join(',');
      const c = raw.code ?? raw.error?.code;
      if (typeof c === 'string') code = c.slice(0, 64);
    }
  } catch { /* đọc hỏng thì giữ câu dự phòng */ }

  report(toast, raw, {
    kind, keys, code, shown,
    toastType: String(toast.type),
    toastId: String(toast.id),
    route: currentRouteTag(),
  }, onIssue);
  return { ...toast, message };
}
